<template>
  <div class="register-mobile-select">
    <label v-if="label" :for="triggerId" class="register-mobile-select__label">
      {{ label }}<span v-if="required" class="register-mobile-select__req"> *</span>
    </label>
    <button
      :id="triggerId"
      type="button"
      class="register-mobile-select__trigger"
      :class="{ 'register-mobile-select__trigger--empty': !hasValue }"
      :aria-expanded="sheetOpen ? 'true' : 'false'"
      @click="openSheet"
    >
      <span class="register-mobile-select__trigger-text">{{ displayText }}</span>
      <v-icon class="register-mobile-select__chevron" size="22">{{ $icons.chevronDown }}</v-icon>
    </button>

    <transition name="register-sheet-fade">
      <div
        v-if="sheetOpen"
        class="register-mobile-select__backdrop"
        @click="closeSheet"
        @touchmove.prevent
      ></div>
    </transition>
    <transition name="register-sheet-slide">
      <div
        v-if="sheetOpen"
        class="register-mobile-select__sheet"
        role="dialog"
        aria-modal="true"
        :aria-label="label || 'Select option'"
      >
        <div class="register-mobile-select__sheet-header">
          <button type="button" class="register-mobile-select__sheet-back" @click="closeSheet">
            <v-icon size="20" aria-hidden="true">{{ $icons.chevronLeft }}</v-icon>
            <span>Back</span>
          </button>
          <h3 class="register-mobile-select__sheet-title">{{ label || 'Select' }}</h3>
          <button
            type="button"
            class="register-mobile-select__sheet-close"
            aria-label="Close"
            @click="closeSheet"
          >
            <v-icon size="22" aria-hidden="true">{{ $icons.close }}</v-icon>
          </button>
        </div>

        <p v-if="multiple" class="register-mobile-select__hint">
          <template v-if="maxSelections">Select up to {{ maxSelections }} options, then press Done.</template>
          <template v-else>Tap one or more options, then press Done.</template>
        </p>
        <p v-if="limitReached" class="register-mobile-select__limit-msg" role="alert">
          Maximum {{ maxSelections }} selections allowed.
        </p>

        <div v-if="searchable" class="register-mobile-select__search-wrap">
          <v-icon class="register-mobile-select__search-icon" size="20" aria-hidden="true">{{ $icons.magnify }}</v-icon>
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="search"
            class="register-mobile-select__search"
            placeholder="Type to search…"
            autocomplete="off"
            enterkeyhint="search"
          />
        </div>

        <ul class="register-mobile-select__list" role="listbox" :aria-multiselectable="multiple ? 'true' : 'false'">
          <li v-for="(item, index) in filteredItems" :key="`${getItemValue(item)}-${index}`">
            <button
              type="button"
              class="register-mobile-select__option"
              :class="{
                'register-mobile-select__option--selected': isSelected(item),
                'register-mobile-select__option--disabled': isOptionDisabled(item),
              }"
              role="option"
              :aria-selected="isSelected(item) ? 'true' : 'false'"
              :aria-disabled="isOptionDisabled(item) ? 'true' : 'false'"
              :disabled="isOptionDisabled(item)"
              @click="onOptionTap(item)"
            >
              <span>{{ getItemText(item) }}</span>
              <v-icon
                v-if="isSelected(item)"
                class="register-mobile-select__check"
                size="20"
                aria-hidden="true"
              >{{ $icons.check }}</v-icon>
            </button>
          </li>
          <li v-if="!filteredItems.length" class="register-mobile-select__empty">No matches found</li>
        </ul>

        <div class="register-mobile-select__footer">
          <button type="button" class="register-mobile-select__done" @click="closeSheet">
            {{ doneLabel }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'RegisterMobileSelect',
  props: {
    value: {
      default: null,
    },
    items: {
      type: Array,
      default: () => [],
    },
    itemValue: {
      type: String,
      default: 'value',
    },
    itemText: {
      type: String,
      default: 'text',
    },
    label: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Tap to select',
    },
    id: {
      type: String,
      default: '',
    },
    maxSelections: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      sheetOpen: false,
      searchQuery: '',
      savedScrollY: 0,
      limitReached: false,
    };
  },
  computed: {
    triggerId() {
      return this.id || `register-mobile-select-${this._uid}`;
    },
    selectableItems() {
      return (this.items || []).filter(
        (item) => item && !item.header && !item.divider && this.getItemValue(item) != null && this.getItemValue(item) !== '',
      );
    },
    filteredItems() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return this.selectableItems;
      return this.selectableItems.filter((item) =>
        String(this.getItemText(item)).toLowerCase().includes(query),
      );
    },
    hasValue() {
      if (this.multiple) return Array.isArray(this.value) && this.value.length > 0;
      return this.value !== null && this.value !== undefined && this.value !== '';
    },
    selectedCount() {
      return Array.isArray(this.value) ? this.value.length : 0;
    },
    isAtMax() {
      if (!this.multiple || this.maxSelections == null) return false;
      return this.selectedCount >= this.maxSelections;
    },
    doneLabel() {
      if (this.multiple) {
        if (this.selectedCount === 0) return 'Done';
        if (this.selectedCount === 1) return 'Done · 1 selected';
        return `Done · ${this.selectedCount} selected`;
      }
      return 'Back to form';
    },
    displayText() {
      if (!this.hasValue) return this.placeholder;
      if (this.multiple) {
        const labels = (this.value || [])
          .map((val) => {
            const match = this.selectableItems.find((item) => this.getItemValue(item) === val);
            return match ? this.getItemText(match) : null;
          })
          .filter(Boolean);
        if (!labels.length) return this.placeholder;
        if (labels.length === 1) return labels[0];
        return `${labels[0]} +${labels.length - 1} more`;
      }
      const selected = this.selectableItems.find((item) => this.getItemValue(item) === this.value);
      return selected ? this.getItemText(selected) : this.placeholder;
    },
  },
  beforeDestroy() {
    this.unlockPageScroll();
  },
  methods: {
    getItemValue(item) {
      return item?.[this.itemValue];
    },
    getItemText(item) {
      return item?.[this.itemText] ?? '';
    },
    isSelected(item) {
      const val = this.getItemValue(item);
      if (this.multiple) return Array.isArray(this.value) && this.value.includes(val);
      return this.value === val;
    },
    lockPageScroll() {
      this.savedScrollY = window.scrollY || window.pageYOffset || 0;
      if (window.scrollX) {
        window.scrollTo(0, this.savedScrollY);
      }
      document.documentElement.classList.add('register-sheet-open');
      document.body.classList.add('register-sheet-open');
      document.body.style.top = `-${this.savedScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    },
    unlockPageScroll() {
      document.documentElement.classList.remove('register-sheet-open');
      document.body.classList.remove('register-sheet-open');
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, this.savedScrollY || 0);
    },
    openSheet() {
      this.searchQuery = '';
      this.limitReached = false;
      this.sheetOpen = true;
      this.lockPageScroll();
    },
    closeSheet() {
      if (!this.sheetOpen) return;
      this.sheetOpen = false;
      this.searchQuery = '';
      this.limitReached = false;
      this.unlockPageScroll();
      this.$emit('change', this.value);
    },
    isOptionDisabled(item) {
      if (!this.isAtMax) return false;
      return !this.isSelected(item);
    },
    onOptionTap(item) {
      if (this.isOptionDisabled(item)) {
        this.limitReached = true;
        return;
      }
      const val = this.getItemValue(item);
      if (this.multiple) {
        const current = Array.isArray(this.value) ? [...this.value] : [];
        const index = current.indexOf(val);
        if (index > -1) {
          current.splice(index, 1);
          this.limitReached = false;
        } else if (this.maxSelections != null && current.length >= this.maxSelections) {
          this.limitReached = true;
          return;
        } else {
          current.push(val);
          this.limitReached = false;
        }
        this.$emit('input', current);
        return;
      }
      this.$emit('input', val);
      this.closeSheet();
    },
  },
};
</script>
